import { useQuery } from "@tanstack/react-query";
import { Fragment, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { PageHeader } from "@/components/PageHeader";
import { RequirePermission } from "@/components/RequirePermission";
import { ErrorState, LoadingState } from "@/components/States";
import { adminAuditApi, adminUsersApi } from "@/lib/api";
import { formatDateTime } from "@/lib/format";

const INPUT_CLS = "w-full px-3 py-2 rounded-md bg-surface border border-border font-mono text-xs";

export default function AuditLog() {
  return (
    <RequirePermission codes="audit.read">
      <AuditInner />
    </RequirePermission>
  );
}

function AuditInner() {
  const { t } = useTranslation();
  const [entityType, setEntityType] = useState<string>("");
  const [action, setAction] = useState<string>("");
  const [actorId, setActorId] = useState<string>("");
  const [entityId, setEntityId] = useState<string>("");
  const [since, setSince] = useState<string>("");
  const [until, setUntil] = useState<string>("");
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  const facetsQ = useQuery({
    queryKey: ["admin", "audit", "facets"],
    queryFn: () => adminAuditApi.facets(),
    staleTime: 60_000,
  });

  const usersQ = useQuery({
    queryKey: ["admin", "users", "all", "for-audit"],
    queryFn: () => adminUsersApi.list({ page_size: 200 }),
    staleTime: 60_000,
  });

  const params = useMemo(
    () => ({
      page,
      page_size: 50,
      entity_type: entityType || undefined,
      action: action || undefined,
      actor_id: actorId || undefined,
      entity_id: entityId || undefined,
      since: since ? new Date(since).toISOString() : undefined,
      until: until ? new Date(until).toISOString() : undefined,
    }),
    [page, entityType, action, actorId, entityId, since, until],
  );

  const q = useQuery({
    queryKey: ["admin", "audit", params],
    queryFn: () => adminAuditApi.list(params),
  });

  const userNameById = useMemo(() => {
    const map: Record<string, string> = {};
    for (const u of usersQ.data?.items ?? []) map[u.id] = u.name + (u.login ? ` (${u.login})` : "");
    return map;
  }, [usersQ.data]);

  function toggle(id: number) {
    setExpanded((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function resetFilters() {
    setEntityType(""); setAction(""); setActorId(""); setEntityId("");
    setSince(""); setUntil(""); setPage(1);
  }

  return (
    <>
      <PageHeader
        eyebrow="admin · audit"
        title={t("audit.title")}
        description={t("audit.description")}
      />

      <div className="card p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 items-end">
        <Field label="entity_type">
          <select className={INPUT_CLS} value={entityType} onChange={(e) => { setPage(1); setEntityType(e.target.value); }}>
            <option value="">{t("audit.allFilter")}</option>
            {facetsQ.data?.entity_types.map((et) => <option key={et} value={et}>{et}</option>)}
          </select>
        </Field>
        <Field label="action">
          <select className={INPUT_CLS} value={action} onChange={(e) => { setPage(1); setAction(e.target.value); }}>
            <option value="">{t("audit.allFilter")}</option>
            {facetsQ.data?.actions.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        </Field>
        <Field label={t("audit.actorLabel")}>
          <select className={INPUT_CLS} value={actorId} onChange={(e) => { setPage(1); setActorId(e.target.value); }}>
            <option value="">{t("audit.anyFilter")}</option>
            {usersQ.data?.items.map((u) => <option key={u.id} value={u.id}>{u.name}{u.login ? ` (${u.login})` : ""}</option>)}
          </select>
        </Field>
        <Field label="entity_id (UUID)">
          <input
            className={INPUT_CLS}
            value={entityId}
            onChange={(e) => { setPage(1); setEntityId(e.target.value); }}
            placeholder="00000000-…"
          />
        </Field>
        <Field label={t("audit.sinceLabel")}>
          <input
            type="datetime-local"
            className={INPUT_CLS}
            value={since}
            onChange={(e) => { setPage(1); setSince(e.target.value); }}
          />
        </Field>
        <Field label={t("audit.untilLabel")}>
          <input
            type="datetime-local"
            className={INPUT_CLS}
            value={until}
            onChange={(e) => { setPage(1); setUntil(e.target.value); }}
          />
        </Field>
        <button className="btn" onClick={resetFilters}>{t("audit.resetFilters")}</button>
      </div>

      {q.isLoading ? (
        <LoadingState />
      ) : q.isError ? (
        <ErrorState error={q.error} />
      ) : (
        <>
          <p className="text-xs text-text-muted font-mono">
            {t("audit.found", { total: q.data!.pagination.total, page: q.data!.pagination.page, pages: q.data!.pagination.pages })}
          </p>
          <div className="card overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-surface-alt">
                <tr className="text-left text-text-muted font-mono text-xs uppercase">
                  <th className="px-3 py-2 w-44">{t("audit.colWhen")}</th>
                  <th className="px-3 py-2">Action</th>
                  <th className="px-3 py-2">Entity</th>
                  <th className="px-3 py-2">Actor</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {q.data!.items.map((r) => (
                  <Fragment key={r.id}>
                    <tr className="border-t border-border hover:bg-surface-hover">
                      <td className="px-3 py-2 text-text-muted font-mono text-xs">{formatDateTime(r.created_at)}</td>
                      <td className="px-3 py-2 font-mono">{r.action}</td>
                      <td className="px-3 py-2">
                        <span className="font-mono text-xs text-text-muted">{r.entity_type}</span>
                        <br />
                        <span className="font-mono text-xs">{r.entity_id}</span>
                      </td>
                      <td className="px-3 py-2 text-xs">
                        {r.actor_id ? (userNameById[r.actor_id] ?? <span className="font-mono">{r.actor_id}</span>) : <span className="text-text-muted">system</span>}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {r.changes && (
                          <button className="btn text-xs" onClick={() => toggle(r.id)}>
                            {expanded.has(r.id) ? "−" : "+"}
                          </button>
                        )}
                      </td>
                    </tr>
                    {expanded.has(r.id) && r.changes && (
                      <tr className="border-t border-border bg-surface-alt/40">
                        <td colSpan={5} className="px-3 py-3">
                          <pre className="text-xs text-text-secondary overflow-x-auto whitespace-pre-wrap">
                            {JSON.stringify(r.changes, null, 2)}
                          </pre>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between pt-2">
            <button className="btn" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>{t("common.back")}</button>
            <span className="text-xs text-text-muted font-mono">{t("audit.pageN", { page })}</span>
            <button
              className="btn"
              disabled={page >= q.data!.pagination.pages}
              onClick={() => setPage((p) => p + 1)}
            >
              {t("audit.next")}
            </button>
          </div>
        </>
      )}
    </>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs font-mono uppercase tracking-wider text-text-muted">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
